import { useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { Repeat2, TrendingUp, TrendingDown, Info, Loader2 } from "lucide-react";
import { api, formatMoney } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";

/**
 * MrrCard — Monthly Recurring Revenue at a glance.
 *
 * iter-211 — pulls `/api/dashboard/mrr`, shows NET MRR, fees deducted,
 * MoM delta chip, and a Dialog with the per-account breakdown.
 *
 * Hidden completely when there are zero recurring receivables. Showing
 * "$0 MRR — Add your first subscription" feels patronising on a freelancer
 * dashboard that has no SaaS clients yet; we just render nothing.
 */
export function MrrCard() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/mrr");
        if (alive) setState({ loading: false, data, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, data: null, error: e });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <Card
        className="p-4 rounded-2xl border-slate-200 dark:border-slate-700 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"
        data-testid="mrr-card-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {isEs ? "Calculando MRR…" : "Computing MRR…"}
      </Card>
    );
  }

  if (state.error || !state.data) return null;
  const { mrr_net, mrr_gross, fees_deducted, currency, delta_mom_pct, count, breakdown_by_account } = state.data;
  if (!count || count === 0) return null;  // zero-state: hide entirely

  const positive = (delta_mom_pct ?? 0) >= 0;
  const deltaLabel = delta_mom_pct == null
    ? (isEs ? "Sin comparativa" : "No prior data")
    : `${positive ? "+" : ""}${delta_mom_pct}%`;

  return (
    <Card
      className="p-5 rounded-2xl border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white"
      data-testid="mrr-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <Repeat2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-800">
              {isEs ? "MRR · Ingresos recurrentes" : "MRR · Recurring revenue"}
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="text-emerald-600 hover:text-emerald-800 transition-colors"
                    aria-label={isEs ? "Detalle MRR" : "MRR detail"}
                    data-testid="mrr-info-trigger"
                  >
                    <Info className="h-3 w-3" />
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="mrr-detail-dialog">
                  <DialogHeader>
                    <DialogTitle>
                      {isEs ? "Detalle del MRR neto" : "Net MRR breakdown"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                      <span className="text-slate-600 dark:text-slate-300">{isEs ? "MRR bruto" : "Gross MRR"}</span>
                      <span className="font-semibold tabular-nums" data-testid="mrr-detail-gross">
                        {formatMoney(mrr_gross, currency)}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-2 text-rose-700">
                      <span>{isEs ? "Comisiones de cuenta" : "Account fees"}</span>
                      <span className="font-semibold tabular-nums" data-testid="mrr-detail-fees">
                        −{formatMoney(fees_deducted, currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-emerald-700 font-semibold">
                      <span>{isEs ? "MRR neto" : "Net MRR"}</span>
                      <span className="tabular-nums" data-testid="mrr-detail-net">
                        {formatMoney(mrr_net, currency)}
                      </span>
                    </div>

                    <div className="pt-3">
                      <div className="text-[11px] uppercase font-semibold text-slate-500 dark:text-slate-400 mb-2">
                        {isEs ? "Por cuenta" : "Per account"}
                      </div>
                      <ul className="space-y-2">
                        {breakdown_by_account.map((b, idx) => (
                          <li
                            key={b.account_id || `none-${idx}`}
                            className="flex items-center justify-between gap-2 text-xs"
                            data-testid={`mrr-breakdown-row-${b.account_id || "none"}`}
                          >
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                {b.account_name || (isEs ? "Sin cuenta asignada" : "No account")}
                              </div>
                              <div className="text-slate-500 dark:text-slate-400">
                                {b.count} {isEs ? (b.count === 1 ? "cobro" : "cobros") : (b.count === 1 ? "item" : "items")}
                                {b.fee_pct > 0 && (
                                  <span className="ml-1 text-rose-600">
                                    • {b.fee_pct}% {isEs ? "comisión" : "fee"}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="font-semibold tabular-nums">
                                {formatMoney(b.net_monthly, currency)}
                              </div>
                              {b.fee_amount > 0 && (
                                <div className="text-[10px] text-slate-400 tabular-nums">
                                  {formatMoney(b.gross_monthly, currency)} bruto
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <p className="text-[11px] text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-800">
                      {isEs
                        ? "Cálculo: Σ cobros recurrentes normalizados a cadencia mensual, convertidos a tu moneda, menos comisiones por cuenta."
                        : "Formula: Σ recurring receivables normalised to monthly cadence, converted to your currency, minus per-account fees."}
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div
              className="font-display text-2xl sm:text-3xl font-bold tabular-nums mt-0.5 text-slate-900 dark:text-slate-100"
              data-testid="mrr-card-net"
            >
              {formatMoney(mrr_net, currency)}
              <span className="text-sm text-slate-500 dark:text-slate-400 ml-1">/{isEs ? "mes" : "mo"}</span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-testid="mrr-card-count">
              {count} {isEs
                ? (count === 1 ? "suscripción activa" : "suscripciones activas")
                : (count === 1 ? "active subscription" : "active subscriptions")}
              {fees_deducted > 0 && (
                <span className="text-slate-400">
                  {" "}· {formatMoney(fees_deducted, currency)} {isEs ? "en comisiones" : "in fees"}
                </span>
              )}
            </div>
          </div>
        </div>

        {delta_mom_pct != null && (
          <div
            className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold flex-shrink-0
              ${positive
                ? "bg-emerald-100 text-emerald-800"
                : "bg-rose-100 text-rose-800"}`}
            data-testid="mrr-card-delta"
            title={isEs ? "Variación mes a mes" : "Month-over-month change"}
          >
            {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {deltaLabel}
          </div>
        )}
      </div>
    </Card>
  );
}
